import React from 'react';
import { FormProvider } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import ApolloError from '@shared/components/ApolloError';
import { DialogActions, DialogContent } from '@mui/material';
import ConfirmDialogWithButton from '@shared/components/confirmDialog/ConfirmDialogWithButton';
import { DIALOG_MODES } from '@shared/components/confirmDialog/dialogWrappers/constants/DialogModes';
import { useNavigate } from 'react-router';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import MotionSection from '@shared/components/animations/MotionSection';
import { AnimatePresence } from 'framer-motion';
import {
  FORM_ID,
  MOVE_TEXT,
  CANCEL_TEXT,
  SAVE_TEXT,
  USER_MOVED_SUCCESSFULLY_MESSAGE,
  MOVE_CONFIRMATION_MESSAGE,
  FORM_INCOMPLETE_MESSAGE
} from './constants/moveLicenseConstants';
import MOVE_USER_LICENSE_TO_ORG from '../../../queries/changeLicenseQueries/moveUserLicenseToOrgMutation';
import { showSuccessToast, showErrorToast } from '@shared/services/toastService';
import GET_MOVE_LICENSE_TO_ORG_DATA from '../../../queries/getMoveLicenseToOrgDataQuery';
import { LinkToast } from '../../../../notifications/toastNotification/components/LinkToast';
import { VALIDATION_ERROR_CODES } from '../../../constants/ValidationErrorCodes';
import { pageUrlService } from '@shared/services/pageUrlService';
import { MoveLicenseToOrgValidator } from './validators/moveLicenseToOrgValidator';
import { FormOrgOrgUnitSelector } from '../../../../shared/components/form/FormOrgOrgUnitSelector';
import FormCustomSelector from '@shared/components/form/FormCustomSelector';
import { isUserOrgTierByRole } from '@shared/services/checkUserTierService';
import { ORG_ORG_UNIT_SELECTOR_MODE } from '@shared/constants/org-org-unit-selector-modes';
import { UserTiers } from '../../../../../constants/userTiers';
import { useFormWithDelayedSubmission } from '../../../../shared/hooks/useFormWithDelayedSubmission';

const METADATA_EVENT_ID_KEY = 'EventId';

/**
 * @typedef {object} MoveLicenseToOrgProps
 * @property {number} orgId
 * @property {number} userRoleId
 * @property {React.ReactNode} [dialogContent]
 * @property {React.ReactNode} [dialogActions]
 * @property {number} [externalUserId]
 * @property {string} [previousLink]
 */

/**
 * @param {MoveLicenseToOrgProps} props
 */
const MoveLicenseToOrg = props => {
  const { orgId, userRoleId, dialogContent, dialogActions, externalUserId, previousLink } = props;

  const { form, verify, handleSubmit } = useFormWithDelayedSubmission({
    resolver: joiResolver(MoveLicenseToOrgValidator.schema),
    defaultValues: {
      systemRoleId: userRoleId,
      selectedOrg: orgId,
      selectedOrgUnit: getDefaultOrgUnitValue(userRoleId)
    },
    onSubmit: handleConfirm,
    onError: onError
  });

  const {
    setValue,
    watch,
    formState: { isSubmitted }
  } = form;

  const isOrgTier = isUserOrgTierByRole(watch('systemRoleId'));
  const orgUnitSelectorMode = isOrgTier
    ? ORG_ORG_UNIT_SELECTOR_MODE.SINGLE_ORG_MULTIPLE_UNIT_SELECTION
    : ORG_ORG_UNIT_SELECTOR_MODE.SINGLE_ORG_SINGLE_UNIT_SELECTION;

  const navigate = useNavigate();
  const client = useApolloClient();

  const {
    data: popUpData,
    loading: popUpDataLoading,
    error: popUpDataError
  } = useQuery(GET_MOVE_LICENSE_TO_ORG_DATA, {
    variables: { tiers: [UserTiers.Org, UserTiers.OrgUnit] }
  });

  const organizations = popUpData
    ? getOrganizationOptions(popUpData.activeMddapOrgsWithOrgUnits, isOrgTier)
    : [];
  const userRoleOptions = popUpData
    ? popUpData.getUserRolesByUserTiers.map(entry => ({
        value: entry.Id,
        title: entry.DisplayName
      }))
    : [];

  const [moveUser, { error: moveUserError }] = useMutation(MOVE_USER_LICENSE_TO_ORG, {
    update: () => client.clearStore()
  });

  async function handleConfirm(formData) {
    const result = await moveUser({
      variables: {
        userLicenseInfo: {
          ExternalUserId: externalUserId,
          SystemRoleId: formData.systemRoleId,
          LicensedOrgId: formData.selectedOrg,
          AllowedOrgUnitIds: isUserOrgTierByRole(formData.systemRoleId)
            ? formData.selectedOrgUnit
            : null,
          LicensedOrgUnitId: !isUserOrgTierByRole(formData.systemRoleId)
            ? formData.selectedOrgUnit
            : null
        }
      }
    });

    if (!result.data.moveUserLicenseToOrg.Success) {
      result.data.moveUserLicenseToOrg.Errors.forEach(err => {
        if (
          err.Code == VALIDATION_ERROR_CODES.FUTURE_ASSIGNED_EVENTS ||
          err.Code == VALIDATION_ERROR_CODES.FUTURE_INVITED_EVENTS
        ) {
          showErrorToast(
            <LinkToast
              message={err.Message}
              url={pageUrlService.getEditEventPageUrl(
                err.Metadata.find(x => x.Key === METADATA_EVENT_ID_KEY).Value
              )}
              openInNewTab={true}
            />
          );

          return;
        }

        showErrorToast(err.Message);
      });

      return;
    }

    showSuccessToast(USER_MOVED_SUCCESSFULLY_MESSAGE);
    navigate(previousLink);
  }

  function onError() {
    showErrorToast(FORM_INCOMPLETE_MESSAGE);
  }

  const onRoleChange = value => {
    setValue('selectedOrgUnit', getDefaultOrgUnitValue(value), {
      shouldValidate: isSubmitted
    });
  };

  if (popUpDataError || moveUserError) {
    return <ApolloError />;
  }

  return (
    <form id={FORM_ID} onSubmit={handleSubmit}>
      <FormProvider {...form}>
        <DialogContent>
          {dialogContent}

          <AnimatePresence>
            <MotionSection keyProp='move-license-to-org'>
              <div className='page-section'>
                <div className='role-selector'>
                  <FormCustomSelector
                    title={<span className='mui-selection-list__label'>User Role*</span>}
                    name='systemRoleId'
                    options={userRoleOptions}
                    loading={popUpDataLoading}
                    onChange={onRoleChange}
                    menuProps={{ disablePortal: true }}
                    key='system-role-id-selector'
                    id='roles-selector'
                    placeholder='Select a User Role'
                    testId='roles-selector'
                  />
                </div>
              </div>

              <FormOrgOrgUnitSelector
                organizations={organizations}
                selectorMode={orgUnitSelectorMode}
                loading={popUpDataLoading}
                sectionClassName='option-selector'
                orgSelectorProps={{
                  label: 'Organization Name(s)',
                  required: true,
                  name: 'selectedOrg'
                }}
                orgUnitSelectorProps={{
                  label: 'OU Name(s)',
                  required:
                    orgUnitSelectorMode ===
                    ORG_ORG_UNIT_SELECTOR_MODE.SINGLE_ORG_SINGLE_UNIT_SELECTION,
                  name: 'selectedOrgUnit'
                }}
              />
            </MotionSection>
          </AnimatePresence>
        </DialogContent>

        <DialogActions>
          {dialogActions}

          <ConfirmDialogWithButton
            openModalBtnText={SAVE_TEXT}
            confirmBtnText={MOVE_TEXT}
            cancelBtnText={CANCEL_TEXT}
            message={MOVE_CONFIRMATION_MESSAGE}
            dialogMode={DIALOG_MODES.EXTERNAL_FORM_SUBMIT}
            formId={FORM_ID}
            openModalPreCondition={verify}
          />
        </DialogActions>
      </FormProvider>
    </form>
  );
};

const getOrganizationOptions = (orgsWithOrgUnits, isOrgTier) => {
  if (isOrgTier) {
    return orgsWithOrgUnits;
  }

  return orgsWithOrgUnits.map(org => ({
    ...org,
    MddapOrgUnits: org.MddapOrgUnits.filter(orgUnit => orgUnit.IsActive)
  }));
};

const getDefaultOrgUnitValue = roleId => (isUserOrgTierByRole(roleId) ? [] : null);

export default MoveLicenseToOrg;
